// src/hooks/useClipboardPaste.ts
import { useEffect, useRef } from 'react';
import { Line, TextNote, ImageElement, DrawingState } from '../components/Canvas/types';

export type ParsedClipboardData = { 
  lines?: Line[]; 
  textNotes?: TextNote[]; 
  images?: ImageElement[] 
};

/** Хранит последние координаты мыши над canvas */
let lastMouseOverCanvas: { x: number; y: number } | null = null;

export const useClipboardPaste = (
  onPaste: (data: ParsedClipboardData, center: { x: number; y: number }) => void
) => {
  const onPasteRef = useRef(onPaste);
  onPasteRef.current = onPaste;

  useEffect(() => {
    const canvas = document.querySelector('canvas') as HTMLCanvasElement | null;
    if (!canvas) return;

    const updateMouse = (e: MouseEvent) => {
      const r = canvas.getBoundingClientRect();
      const x = e.clientX - r.left;
      const y = e.clientY - r.top;
      lastMouseOverCanvas = { x, y };
    };

    const clearMouse = () => {
      lastMouseOverCanvas = null;
    };

    canvas.addEventListener('mousemove', updateMouse);
    canvas.addEventListener('mouseleave', clearMouse);

    return () => {
      canvas.removeEventListener('mousemove', updateMouse);
      canvas.removeEventListener('mouseleave', clearMouse);
    };
  }, []);

  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items || items.length === 0) return;
      
      let plainItem: DataTransferItem | null = null;
      let imageItem: DataTransferItem | null = null;
      
      for (let i = 0; i < items.length; i++) {
        if (items[i].type === 'text/plain') {
          plainItem = items[i];
        } else if (items[i].type.startsWith('image/')) {
          imageItem = items[i];
        }
      }
      
      // Сначала пытаемся обработать изображение
      if (imageItem) {
        const blob = imageItem.getAsFile();
        if (blob) {
          const pos = lastMouseOverCanvas ?? getViewportCenter();
          const reader = new FileReader();
          reader.onload = (e) => {
            const src = e.target?.result as string;
            const img = new Image();
            img.onload = () => {
              // Ограничиваем размеры изображения
              const maxSize = 300;
              let width = img.width;
              let height = img.height;
              
              if (width > height) {
                if (width > maxSize) {
                  height = (height / width) * maxSize;
                  width = maxSize;
                }
              } else {
                if (height > maxSize) {
                  width = (width / height) * maxSize;
                  height = maxSize;
                }
              }
              
              onPasteRef.current(
                { 
                  images: [{
                    id: `image-${Date.now()}`,
                    src,
                    x: pos.x - width / 2,
                    y: pos.y - height / 2,
                    width,
                    height,
                    originalWidth: img.width,
                    originalHeight: img.height
                  }] 
                },
                { x: pos.x, y: pos.y }
              );
            };
            img.src = src;
          };
          reader.readAsDataURL(blob);
        }
        e.preventDefault();
        return;
      }
      
      // Если изображения нет, обрабатываем текст
      if (plainItem) {
        plainItem.getAsString((str) => {
          try {
            const parsed: ParsedClipboardData = JSON.parse(str);
            const pos = lastMouseOverCanvas ?? getViewportCenter();
            onPasteRef.current(parsed, pos);
          } catch {
            const text = str.trim();
            if (text) {
              const pos = lastMouseOverCanvas ?? getViewportCenter();
              onPasteRef.current(
                { textNotes: [createTextNote(text, pos.x, pos.y)] },
                pos
              );
            }
          }
        });
      }
      e.preventDefault();
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, []);
};

/* ------------------ helpers ------------------ */
function getViewportCenter(): { x: number; y: number } {
  const canvas = document.querySelector('canvas') as HTMLCanvasElement | null;
  if (!canvas) return { x: 0, y: 0 };
  const r = canvas.getBoundingClientRect();
  return { x: r.width / 2, y: r.height / 2 };
}

function createTextNote(text: string, x: number, y: number): TextNote {
  return {
    id: 'paste-text-' + Date.now(),
    text,
    x,
    y,
    fontSize: 16 * 3,
    color: '#000000',
    width: 200,
    height: 50,
  };
}